import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Plus, Trash2, FileText, TestTube, Heart, Brain, Eye, 
  Microscope, Dna, Activity, Upload, Calendar, ChevronRight
} from "lucide-react";

// Categorie esami con icone
const examCategories = {
  ematologia: { label: "Ematologia", icon: TestTube, color: "bg-red-100 text-red-600" },
  biochimica: { label: "Biochimica", icon: Activity, color: "bg-amber-100 text-amber-600" },
  urine: { label: "Esame Urine", icon: TestTube, color: "bg-yellow-100 text-yellow-600" },
  feci: { label: "Feci/Parassiti", icon: Microscope, color: "bg-green-100 text-green-600" },
  imaging_radiografia: { label: "Radiografia", icon: FileText, color: "bg-blue-100 text-blue-600" },
  imaging_ecografia: { label: "Ecografia", icon: Eye, color: "bg-indigo-100 text-indigo-600" },
  imaging_tac: { label: "TAC", icon: Brain, color: "bg-purple-100 text-purple-600" },
  imaging_risonanza: { label: "Risonanza", icon: Brain, color: "bg-pink-100 text-pink-600" },
  cardiologia: { label: "Cardiologia", icon: Heart, color: "bg-rose-100 text-rose-600" },
  dermatologia: { label: "Dermatologia", icon: Eye, color: "bg-orange-100 text-orange-600" },
  citologia: { label: "Citologia", icon: Microscope, color: "bg-teal-100 text-teal-600" },
  istologia: { label: "Istologia", icon: Microscope, color: "bg-cyan-100 text-cyan-600" },
  microbiologia: { label: "Microbiologia", icon: Microscope, color: "bg-lime-100 text-lime-600" },
  sierologia: { label: "Sierologia", icon: TestTube, color: "bg-emerald-100 text-emerald-600" },
  genetica: { label: "Test Genetici", icon: Dna, color: "bg-violet-100 text-violet-600" },
  altro: { label: "Altro", icon: FileText, color: "bg-slate-100 text-slate-600" }
};

const resultStatusColors = {
  normal: "bg-emerald-100 text-emerald-700",
  abnormal: "bg-amber-100 text-amber-700",
  critical: "bg-red-100 text-red-700"
};

export default function MedicalExams({ petId, petName }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddExam, setShowAddExam] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (petId) fetchExams();
  }, [petId]);

  const fetchExams = async () => {
    try {
      const res = await authFetch(`${API}/exams?pet_id=${petId}`);
      if (res.ok) {
        const data = await res.json();
        setExams(data);
      }
    } catch (error) {
      console.error("Error fetching exams:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExam = async (examId) => {
    if (!confirm("Eliminare questo esame?")) return;
    try {
      const res = await authFetch(`${API}/exams/${examId}`, { method: 'DELETE' });
      if (res.ok) {
        setExams(exams.filter(e => e.exam_id !== examId));
        toast.success("Esame eliminato");
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  // Raggruppa esami per categoria
  const examsByCategory = exams.reduce((acc, exam) => {
    const cat = exam.exam_type || 'altro';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(exam);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#FF6B4A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="medical-exams-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-[#1D1D1F]">Esami e Analisi</h2>
        <Button onClick={() => setShowAddExam(true)} className="bg-[#1D1D1F] rounded-xl">
          <Plus size={18} className="mr-2" /> Aggiungi esame
        </Button>
      </div>

      {/* Categorie con esami */}
      {exams.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-3xl border border-black/5">
          <TestTube className="mx-auto text-[#D1D1D6] mb-4" size={48} />
          <h3 className="font-semibold text-[#1D1D1F] mb-2">Nessun esame registrato</h3>
          <p className="text-[#86868B] mb-4">Aggiungi esami del sangue, ecografie, radiografie...</p>
          <Button onClick={() => setShowAddExam(true)} variant="outline" className="rounded-xl">
            <Plus size={18} className="mr-2" /> Aggiungi primo esame
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(examsByCategory).map(([category, categoryExams]) => {
            const catConfig = examCategories[category] || examCategories.altro;
            const Icon = catConfig.icon;
            
            return (
              <div key={category} className="bg-white rounded-2xl p-4 shadow-sm border border-black/5">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${catConfig.color}`}>
                      <Icon size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1D1D1F]">{catConfig.label}</p>
                      <p className="text-sm text-[#86868B]">{categoryExams.length} esami</p>
                    </div>
                  </div>
                  <ChevronRight 
                    size={20} 
                    className={`text-[#86868B] transition-transform ${selectedCategory === category ? 'rotate-90' : ''}`} 
                  />
                </div>

                {/* Esami espansi */}
                {selectedCategory === category && (
                  <div className="mt-4 space-y-2 border-t border-black/5 pt-4">
                    {categoryExams.sort((a, b) => new Date(b.exam_date) - new Date(a.exam_date)).map(exam => (
                      <div 
                        key={exam.exam_id} 
                        className="bg-[#F5F5F7] rounded-xl p-3 flex items-center justify-between group"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#1D1D1F]">{exam.exam_name}</p>
                            {exam.result_status && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${resultStatusColors[exam.result_status] || resultStatusColors.normal}`}>
                                {exam.result_status === 'normal' ? 'Normale' : 
                                 exam.result_status === 'abnormal' ? 'Anomalo' : 'Critico'}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#86868B]">
                            {new Date(exam.exam_date).toLocaleDateString('it-IT')}
                            {exam.laboratory && ` • ${exam.laboratory}`}
                          </p>
                          {exam.result_summary && (
                            <p className="text-xs text-[#86868B] mt-1 line-clamp-2">{exam.result_summary}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {exam.file_url && (
                            <button
                              onClick={() => window.open(`${API.replace('/api', '')}${exam.file_url}`, '_blank')}
                              className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                            >
                              <FileText size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteExam(exam.exam_id)}
                            className="p-2 text-[#86868B] hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Exam Dialog */}
      <AddExamDialog 
        petId={petId}
        open={showAddExam}
        onClose={() => setShowAddExam(false)}
        onSuccess={() => {
          setShowAddExam(false);
          fetchExams();
        }}
      />
    </div>
  );
}


// Dialog per aggiungere esame
function AddExamDialog({ petId, open, onClose, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    exam_type: "",
    exam_name: "",
    exam_date: "",
    result_summary: "",
    result_status: "",
    laboratory: "",
    vet_name: "",
    notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await authFetch(`${API}/exams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pet_id: petId,
          ...formData
        })
      });

      if (res.ok) {
        toast.success("Esame aggiunto!");
        setFormData({
          exam_type: "",
          exam_name: "",
          exam_date: "",
          result_summary: "",
          result_status: "",
          laboratory: "",
          vet_name: "",
          notes: ""
        });
        onSuccess?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[500px] rounded-3xl bg-white p-0 border-0 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <DialogHeader className="mb-5">
            <DialogTitle className="text-xl font-bold text-[#1D1D1F] flex items-center gap-3">
              <TestTube className="text-[#FF6B4A]" size={24} />
              Aggiungi Esame
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Categoria */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Tipo esame *</Label>
              <Select 
                value={formData.exam_type} 
                onValueChange={(v) => setFormData({...formData, exam_type: v})}
              >
                <SelectTrigger className="h-12 rounded-xl bg-[#F5F5F7] border-0" data-testid="exam-type-select">
                  <SelectValue placeholder="Seleziona categoria..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(examCategories).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <config.icon size={16} />
                        {config.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Nome esame */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Nome esame *</Label>
              <Input
                value={formData.exam_name}
                onChange={(e) => setFormData({...formData, exam_name: e.target.value})}
                placeholder="Es. Emocromo completo, Ecografia addominale..."
                className="h-12 rounded-xl bg-[#F5F5F7] border-0"
                required
                data-testid="exam-name-input"
              />
            </div>

            {/* Data */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Data esame *</Label>
              <Input
                type="date"
                value={formData.exam_date}
                onChange={(e) => setFormData({...formData, exam_date: e.target.value})}
                className="h-12 rounded-xl bg-[#F5F5F7] border-0"
                required
                data-testid="exam-date-input"
              />
            </div>

            {/* Risultato */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Esito</Label>
              <Select 
                value={formData.result_status} 
                onValueChange={(v) => setFormData({...formData, result_status: v})}
              >
                <SelectTrigger className="h-12 rounded-xl bg-[#F5F5F7] border-0" data-testid="result-status-select">
                  <SelectValue placeholder="Seleziona esito..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normale</SelectItem>
                  <SelectItem value="abnormal">Anomalo</SelectItem>
                  <SelectItem value="critical">Critico</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Riepilogo risultati */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Note/Riepilogo risultati</Label>
              <Textarea
                value={formData.result_summary}
                onChange={(e) => setFormData({...formData, result_summary: e.target.value})}
                placeholder="Descrivi i risultati principali..."
                className="rounded-xl bg-[#F5F5F7] border-0 min-h-[80px]"
                data-testid="result-summary-textarea"
              />
            </div>

            {/* Laboratorio e Veterinario */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold mb-2 block">Laboratorio</Label>
                <Input
                  value={formData.laboratory}
                  onChange={(e) => setFormData({...formData, laboratory: e.target.value})}
                  placeholder="Nome laboratorio"
                  className="h-12 rounded-xl bg-[#F5F5F7] border-0"
                  data-testid="laboratory-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold mb-2 block">Veterinario</Label>
                <Input
                  value={formData.vet_name}
                  onChange={(e) => setFormData({...formData, vet_name: e.target.value})}
                  placeholder="Nome veterinario"
                  className="h-12 rounded-xl bg-[#F5F5F7] border-0"
                  data-testid="vet-name-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting || !formData.exam_type || !formData.exam_name || !formData.exam_date}
              className="w-full h-12 bg-[#1D1D1F] rounded-xl"
              data-testid="submit-exam-btn"
            >
              {isSubmitting ? "Salvataggio..." : "Aggiungi esame"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
